import { useEffect, useMemo, useState } from 'react';
import { formatDate, parseIsoDateTime } from '@ninja/utilities';
import { Gender, Relationship } from '../../../common';
import { selectAppInitLoading, selectMembers, useAppInitStore, useQuoteStore } from '../../../stores';
import { useRelationships } from '../../app-init-data';

type MemberFamilyCounts = {
	wife: number;
	husband: number;
	children: number;
	son: number;
	daughter: number;
	parents: number;
	father: number;
	mother: number;
	worker: number;
};

const initialMemberFamilyCounts = {
	wife: 0,
	husband: 0,
	children: 0,
	son: 0,
	daughter: 0,
	parents: 0,
	father: 0,
	mother: 0,
	worker: 0,
};

type MappedMember = { relation: string; gender: string; dateOfBirth: string; nationality: string };

export const useCoveredMembers = () => {
	const journeyDetails = useQuoteStore(selectMembers);
	const { getRelationshipById, relationships } = useRelationships();
	const isAppInitLoading = useAppInitStore(selectAppInitLoading);
	const [mappedMembers, setMappedMembers] = useState<MappedMember[]>([]);

	useEffect(() => {
		if (isAppInitLoading) return;

		const members = journeyDetails?.dependents?.map((dependent) => {
			return {
				relation: dependent?.relationship ?? '',
				gender: dependent?.gender ?? '',
				dateOfBirth: formatDate(parseIsoDateTime(dependent?.dateOfBirth)) ?? '',
				nationality: dependent?.nationality ?? '',
			};
		});

		setMappedMembers(members ?? []);
	}, [journeyDetails, isAppInitLoading]);

	const memberFamilyCounts: MemberFamilyCounts = useMemo(() => {
		return (
			journeyDetails?.dependents?.reduce(
				(acc, curr) => {
					if (curr.relationshipTypeId === Relationship.Spouse && curr.genderId === Gender.Female) {
						return {
							...acc,
							wife: acc.wife + 1,
						};
					}

					if (curr.relationshipTypeId === Relationship.Spouse && curr.genderId === Gender.Male) {
						return {
							...acc,
							husband: acc.husband + 1,
						};
					}

					if (curr.relationshipTypeId === Relationship.Son || curr.relationshipTypeId === Relationship.Daughter) {
						acc.children += 1;

						if (curr.relationshipTypeId === Relationship.Son) {
							acc.son += 1;
						}

						if (curr.relationshipTypeId === Relationship.Daughter) {
							acc.daughter += 1;
						}

						return acc;
					}

					if (curr.relationshipTypeId === Relationship.Father || curr.relationshipTypeId === Relationship.Mother) {
						acc.parents += 1;

						if (curr.relationshipTypeId === Relationship.Father) {
							acc.father += 1;
						}

						if (curr.relationshipTypeId === Relationship.Mother) {
							acc.mother += 1;
						}

						return acc;
					}

					if (curr.relationshipTypeId === Relationship.DomesticWorker) {
						return {
							...acc,
							worker: acc.worker + 1,
						};
					}

					return acc;
				},
				{ ...initialMemberFamilyCounts }
			) ?? initialMemberFamilyCounts
		);
	}, [journeyDetails]);

	const depenDents = journeyDetails?.dependents?.map((dependent) => {
		const depenDents = getRelationshipById(dependent.relationshipTypeId);

		return {
			...depenDents,
		};
	});

	const initialCoveredMembers = relationships.reduce((acc, curr) => {
		acc[curr.name] = 0;

		return acc;
	}, {} as Record<string, number>);

	const coveredMembers = depenDents?.reduce((acc, curr) => {
		Object.keys(acc).forEach((key) => {
			if (curr.name === key) {
				acc[key] += 1;
			}
		});

		return acc;
	}, initialCoveredMembers);

	return {
		coveredMembers,
		mappedMembers,
		memberFamilyCounts,
		journeyDetails,
	};
};
